import { type SvgIconProps, Box } from '@shared/components';
import type { ChainedCommands, Commands } from '@tiptap/core';

import { type Editor, useRichTextEditor } from '../../providers';

import ControlButton from './control-button';

export interface RichTextEditorControlProps<TOperation extends keyof ChainedCommands> {
  /**
   * The label of the control
   */
  label: string;
  /**
   * The icon of the control
   */
  icon: (props: SvgIconProps) => JSX.Element;
  /**
   * The operation of the control.
   * The operation name should be a valid method name of the `ChainedCommands` type.
   */
  operation: {
    name: TOperation;
    attributes?: NonNullable<Parameters<ChainedCommands[TOperation]>[number]>;
  };
  /**
   * The active state of the control.
   * The active state name should be a valid method name of the `Commands` type.
   */
  active?: { name: keyof Commands; attributes?: Record<string, unknown> | string };
  /**
   * The disabled state of the control.
   */
  disabled?: (editor: Editor) => boolean;
}

// NOTE: This function is used to execute the operation of the control.
// Using ts-expect-error to bypass the type error because the type of the editor is weird.
function executeOperation<TOperation extends keyof ChainedCommands>(
  editor: Editor,
  operation: RichTextEditorControlProps<TOperation>['operation'],
) {
  if (!operation.attributes) {
    // @ts-expect-error: Editor types are weird. It requires at least 2 arguments but some methods don't need it.
    return (editor.chain().focus()[operation.name]() as ChainedCommands).run();
  }

  if (typeof operation.attributes !== 'object') {
    // @ts-expect-error: Editor types are weird. It requires at least 2 arguments but some methods don't need it.
    return (editor.chain().focus()[operation.name](operation.attributes) as ChainedCommands).run();
  }

  return (
    editor
      .chain()
      .focus()
      [operation.name](
        // @ts-expect-error: Editor types are weird. It requires at least 2 arguments but some methods don't need it.
        ...operation.attributes,
      ) as ChainedCommands
  ).run();
}

export default function RichTextEditorControl<TOperation extends keyof ChainedCommands>(
  props: Readonly<RichTextEditorControlProps<TOperation>>,
) {
  const { editor } = useRichTextEditor();
  const { label, icon: Icon, operation, active: isActive, disabled: isDisabled } = props;

  if (!editor) {
    return null;
  }

  return (
    <ControlButton
      aria-label={label}
      data-rich-text-editor-control
      disabled={isDisabled?.(editor) || false}
      onClick={() => {
        executeOperation(editor, operation);
      }}
      data-disabled={isDisabled?.(editor) || false || undefined}
      data-active={
        (isActive?.name ? editor?.isActive(isActive.name, isActive.attributes) : false) || undefined
      }
    >
      <Icon />
      <Box className='sr-only' component='span' sx={{ display: 'none' }}>
        {label}
      </Box>
    </ControlButton>
  );
}
